import {ComponentStore} from '@ngrx/component-store';
import {Injectable} from '@angular/core';
import {ReportDashboardWidgetChartState} from './report-dashboard-widget-chart';
import {Observable, Subscription, withLatestFrom} from 'rxjs';
import {EChartsOption} from 'echarts';
import {DecimalService} from '../../services/decimal.service';
import {CurrencyService} from '../../services/currency.service';
import {PeriodsService} from '../../services/periods.service';
import {ReportDashboardWidgetStore} from './report-dashboard-widget.store';
import {Serializer} from '../../interfaces/serializer';
import {User} from '../../interfaces/user';
import {AppState} from '../store';
import { Store } from '@ngrx/store';
import {selectUser} from '../init/init.selectors';
import {AdReportParams, AdRequestParams, AdRReport, AdRResponseRow} from '../../interfaces/ad-reports';
import {Metric} from '../../interfaces/metrics';
import {EchartsUtils} from '../../libraries/echarts-utils';
import {ReportDashboardWidgetState} from './report-dashboard';
import {tap} from 'rxjs/operators';

@Injectable()
export class ReportDashboardWidgetChartStore extends ComponentStore<ReportDashboardWidgetChartState> {
  private readonly _user$: Observable<Serializer<User>> = this._store.select(selectUser);

  public readonly chartOption$: Observable<EChartsOption> = this.select((state: ReportDashboardWidgetChartState): EChartsOption => state.chartOption);

  private readonly _initChartOption$: Subscription = this.effect((): Observable<[AdRReport | Array<AdRReport>, ReportDashboardWidgetState, Serializer<User>]> => this._reportDashboardWidgetStore.data$.pipe(
    withLatestFrom(this._reportDashboardWidgetStore.state$, this._user$),
    tap(([_, reportDashboardWidgetState, user]: [AdRReport | Array<AdRReport>, ReportDashboardWidgetState, Serializer<User>]): void => {
      this.patchState({
        chartOption: this._getChartOption(user, reportDashboardWidgetState)
      });
    })
  ));

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _reportDashboardWidgetStore: ReportDashboardWidgetStore,
    private readonly _decimalService:  DecimalService,
    private readonly _currencyService: CurrencyService,
    private readonly _periodsService: PeriodsService
  ) {
    super({
      chartOption: {}
    });
  }

  private _getChartOption(user: Serializer<User>, widgetState: ReportDashboardWidgetState): EChartsOption {
    let chartOption: EChartsOption = {};

    switch (widgetState.widget.config.type) {
      case 'axis':
        const dataAxis: Array<AdRReport> = widgetState.data as Array<AdRReport>;
        const requestParamsAxis: Array<Partial<AdRequestParams>> = widgetState.requestParams as Array<Partial<AdRequestParams>>;
        const reportParamsAxis: Array<Partial<AdReportParams>> = widgetState.reportParams as Array<Partial<AdReportParams>>;

        if (dataAxis) {
          const xAxisData: Array<string> = [];
          const dimensions: Array<any> = [];
          const yAxis: Array<any> = [];
          const series: Array<any> = [];
          const legend: Array<any> = [];

          for (let i: number = 0; i < widgetState.widget.config.data.length; i++) {
            if ('parameters' in dataAxis[i]) {
              const metric: Metric = dataAxis[i].parameters.columns.metrics.find(metric => metric.slug === widgetState.widget.config.data[i].metrics[0]);

              if (metric) {
                yAxis.push({
                  type: 'value',
                  name: (i + 1) + ' - ' + widgetState.widget.config.data[i].name,
                  position: i === 0 && 'left' || 'right',
                  axisLine: {
                    show: true,
                    lineStyle: {
                      color: EchartsUtils.colors[i]
                    }
                  },
                  axisLabel: {
                    formatter: value => metric.format_type === 'currency' && this._currencyService.format(user, value, dataAxis[i].parameters.currency, 1) || this._decimalService.format(user, value, '1.0')
                  }
                });

                legend.push((i + 1) + ' - ' + widgetState.widget.config.data[i].name);

                const serie: any = {
                  name: (i + 1) + ' - ' + widgetState.widget.config.data[i].name,
                  type: widgetState.widget.config.data[i].type,
                  smooth: true,
                  yAxisIndex: !widgetState.widget.config.sameAxis && i || 0,
                  data: []
                };

                if (widgetState.widget.config.data[i].area) {
                  serie.areaStyle = {};
                }

                series.push(serie);

                for (const item of dataAxis[i].rows) {
                  if (!dimensions.find(dim => item.dimensions[widgetState.widget.config.data[i].dimensions[0]].value === dim[widgetState.widget.config.data[i].dimensions[0]].value)) {
                    dimensions.push(item.dimensions);
                  }
                }
              }
            }
          }

          this._periodsService.sort(dimensions, widgetState.widget.config.data[0].dimensions[0]);

          for (const dimension of dimensions) {
            for (let i = 0; i < widgetState.widget.config.data.length; i++) {
              if ('rows' in dataAxis[i]) {
                const value: any = dataAxis[i].rows.find((item: AdRResponseRow): boolean =>
                  item.dimensions[widgetState.widget.config.data[i].dimensions[0]].value === dimension[widgetState.widget.config.data[i].dimensions[0]].value
                );

                if (value && value.metrics[widgetState.widget.config.data[i].metrics[0]].value) {
                  series[i].data.push(value.metrics[widgetState.widget.config.data[i].metrics[0]]);
                } else {
                  series[i].data.push({value: 0, formatted_value: 0});
                }
              }
            }

            xAxisData.push(dimension[widgetState.widget.config.data[0].dimensions[0]].formatted_value);
          }

          chartOption = {
            color: EchartsUtils.colors,
            tooltip: {
              trigger: 'axis',
              axisPointer: {
                type: 'cross',
                label: {
                  formatter: (value) => {
                    if (value.axisDimension === 'y') {
                      return yAxis[value.axisIndex].axisLabel.formatter((value.value as number).toFixed(2));
                    }
                    return value.value as string;
                  }
                }
              },
              formatter: EchartsUtils.tooltipFormatterMultiple
            },
            legend: {
              show: false,
              data: legend
            },
            grid: {
              left: 100,
              right: 100,
              top: 30,
              bottom: 30
            },
            xAxis: [
              {
                type: 'category',
                axisTick: {
                  alignWithLabel: true
                },
                data: xAxisData
              }
            ],
            yAxis: yAxis,
            series: series
          };
        }
        break;
      case 'pie':
        const dataPie: AdRReport = widgetState.data as AdRReport;
        const requestParamsPie: Partial<AdRequestParams> = widgetState.requestParams as Partial<AdRequestParams>;
        const reportParamsPie: Partial<AdReportParams> = widgetState.reportParams as Partial<AdReportParams>;

        if (dataPie) {
          const data: Array<any> = [];

          for (const row of dataPie.rows) {
            if (row.metrics[widgetState.widget.config.metrics[0]] && row.dimensions[widgetState.widget.config.dimensions[0]]) {
              row.metrics[widgetState.widget.config.metrics[0]].name = row.dimensions[widgetState.widget.config.dimensions[0]].value;

              row.metrics[widgetState.widget.config.metrics[0]].value = row.metrics[widgetState.widget.config.metrics[0]].value || 0;
              row.metrics[widgetState.widget.config.metrics[0]].formatted_value = row.metrics[widgetState.widget.config.metrics[0]].formatted_value || '0';

              data.push(row.metrics[widgetState.widget.config.metrics[0]]);
            }
          }

          chartOption = {
            color: EchartsUtils.colors,
            tooltip: {
              trigger: 'item',
              formatter: EchartsUtils.tooltipFormatterSimple
            },
            legend: {
              show: false,
              type: 'scroll',
              right: 'right',
              data: [],
              orient: 'vertical'
            },
            series: [
              {
                type: 'pie',
                data: data
              }
            ]
          }
        }
        break;
    }

    return chartOption;
  }

}
